/**
 * ScanCheckInPage
 *
 * Lands here when a staff member scans a patient's wallet membership card
 * (NFC/QR → "<app>/scan/:token"). It resolves the token via the API — which
 * auto-checks-in the patient's appointment for today — then redirects to the
 * patient's detail page with the Appointments tab active.
 *
 * Protected route: staff are already logged into the browser, so apiClient
 * sends their JWT + X-Target-Company.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';

import { checkinService } from '@/services/checkin.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';

export function ScanCheckInPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; guard so we resolve once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setError('Missing check-in code.');
      return;
    }

    let cancelled = false;
    checkinService
      .resolve(token)
      .then((res) => {
        if (cancelled) return;
        if (res.checkedIn) {
          toast.success('Patient checked in');
        }
        navigate(res.redirect || `/patients/${res.patientID}?section=appointments`, {
          replace: true,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getErrorMessage(err, 'Could not resolve this membership card.'));
      });

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
      {error ? (
        <>
          <AlertTriangle className="h-10 w-10 text-amber-500" />
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Check-in failed</h1>
            <p className="mt-1 max-w-sm text-sm text-slate-600">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/patients', { replace: true })}
            className="mt-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Go to patients
          </button>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <p className="text-sm font-medium text-slate-600">Checking in…</p>
        </>
      )}
    </div>
  );
}
